import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useSiteContent } from "../data/content";
import LanguageToggle from "./LanguageToggle";
import logoGrey from "../data/pictures/Logo_grey.png";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("profil");
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";
  const content = useSiteContent();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 16);
      if (!onHome) return;
      const sections = content.nav.map((n) => document.getElementById(n.id));
      const y = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i] && sections[i].offsetTop <= y) {
          setActive(content.nav[i].id);
          break;
        }
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onHome, content.nav]);

  const go = (id) => {
    setOpen(false);
    if (!onHome) {
      navigate(`/#${id}`);
      return;
    }
    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,box-shadow] duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[104px] flex items-center justify-between">
        <button
          onClick={() => go("top")}
          className="flex items-center gap-3 group"
          aria-label="Zur Startseite"
        >
          <img
            src={logoGrey}
            alt="Liesegang Consulting"
            className="h-20 w-auto object-contain"
          />
          <span className="hidden sm:flex flex-col leading-tight border-l border-slate-200 pl-3">
            <span className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-medium">
              {content.brandTagline}
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {content.nav.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              className={`px-3.5 py-2 rounded-md text-sm transition-colors duration-200 ${
                active === n.id
                  ? "text-[#0B1F3A] font-medium"
                  : "text-slate-600 hover:text-[#0B1F3A]"
              }`}
            >
              {n.label}
              {active === n.id && (
                <span className="block h-[2px] w-5 bg-teal-500 mt-1 mx-auto rounded-full" />
              )}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("kontakt")}
            className="hidden md:inline-flex items-center gap-1.5 bg-[#0B1F3A] hover:bg-[#102a4d] text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors duration-200"
          >
            {content.ui.contactBtn}
            <ArrowUpRight className="w-4 h-4" />
          </button>
          <div className="hidden md:block">
            <LanguageToggle />
          </div>
          <button
            className="lg:hidden p-2 rounded-md text-slate-700 hover:bg-slate-100"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menü"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-6 py-4 flex flex-col">
            {content.nav.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                className="text-left py-3 text-slate-700 hover:text-[#0B1F3A] border-b border-slate-100 last:border-0"
              >
                {n.label}
              </button>
            ))}
            <button
              onClick={() => go("kontakt")}
              className="mt-4 bg-[#0B1F3A] text-white px-4 py-3 rounded-lg text-sm font-medium"
            >
              {content.ui.contactBtn}
            </button>
            <div className="mt-4 flex justify-center">
              <LanguageToggle />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
